import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard, 
  DollarSign, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  LogIn
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import CouponInput from './CouponInput';

const API = process.env.REACT_APP_BACKEND_URL;

const DirectCheckout = ({ 
  tier, 
  billingCycle, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [paymentGateway, setPaymentGateway] = useState('stripe');
  const [processing, setProcessing] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  // Pre-fill email when user is logged in
  useEffect(() => {
    if (user?.email && !customerInfo.email) {
      setCustomerInfo(prev => ({
        ...prev,
        email: user.email,
        firstName: user.first_name || '',
        lastName: user.last_name || ''
      }));
    }
  }, [user]);
  const [currencyRate, setCurrencyRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [discountCalculation, setDiscountCalculation] = useState(null);
  const [affiliateCode, setAffiliateCode] = useState(null);

  const basePrice = tier ? (billingCycle === 'annual' ? tier.price_annual : tier.price_monthly) : 0;
  const discountedPrice = discountCalculation ? discountCalculation.final_amount : basePrice;
  const currency = paymentGateway === 'chapa' ? 'ETB' : 'USD';
  
  // Calculate converted price using real-time rates (apply to discounted price)
  const convertedPrice = paymentGateway === 'chapa' && currencyRate 
    ? Math.round(discountedPrice * currencyRate.rate * 100) / 100  // Round to 2 decimal places
    : discountedPrice;

  // Fetch real-time currency rate when payment method changes
  useEffect(() => {
    if (paymentGateway === 'chapa' && isOpen) {
      fetchCurrencyRate();
    }
  }, [paymentGateway, isOpen]);

  // Check for affiliate tracking when component opens
  useEffect(() => {
    if (isOpen) {
      detectAffiliateTracking();
    }
  }, [isOpen]);

  const detectAffiliateTracking = () => {
    // Check URL parameters for affiliate code
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref') || urlParams.get('affiliate') || urlParams.get('aff');
    
    // Check local storage for stored affiliate tracking (using 'affiliate_ref' to match App.js)
    const storedAffiliateCode = localStorage.getItem('affiliate_ref');
    const storedClickId = localStorage.getItem('click_id');
    
    if (refCode) {
      setAffiliateCode(refCode);
      // Store for later use (using 'affiliate_ref' to match App.js)
      localStorage.setItem('affiliate_ref', refCode);
      if (urlParams.get('click_id')) {
        localStorage.setItem('click_id', urlParams.get('click_id'));
      }
    } else if (storedAffiliateCode) {
      setAffiliateCode(storedAffiliateCode);
    }
  };

  const fetchCurrencyRate = async () => {
    try {
      setRateLoading(true);
      const response = await axios.get(`${API}/api/currency/usd-to-etb`);
      setCurrencyRate(response.data);
    } catch (error) {
      console.error('Error fetching currency rate:', error);
      // Fallback to approximate rate
      setCurrencyRate({
        rate: 120,
        description: '1 USD ≈ 120 ETB (estimated)',
        source: 'Fallback rate'
      });
      toast.warning('Using estimated exchange rate');
    } finally {
      setRateLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleDiscountApplied = (discountCalc) => {
    setDiscountCalculation(discountCalc);
  };

  const handleDiscountRemoved = () => {
    setDiscountCalculation(null);
  };

  const processStripeCheckout = async () => {
    try {
      const checkoutData = {
        amount: discountedPrice, // Use discounted price
        currency: 'USD',
        product_name: `${tier?.display_name || 'Subscription'} (${billingCycle})`,
        description: `${tier?.description || 'Subscription plan'} - Billed ${billingCycle}`,
        email: customerInfo.email,
        metadata: {
          type: 'subscription',
          tier_id: tier?.id || 'unknown',
          billing_cycle: billingCycle,
          user_email: customerInfo.email,
          original_amount: basePrice,
          discount_amount: discountCalculation?.discount_amount || 0,
          coupon_code: discountCalculation?.coupon?.code || null,
          affiliate_code: affiliateCode || null,
          click_id: localStorage.getItem('click_id') || null
        }
      };

      const response = await axios.post(`${API}/api/payments/stripe/checkout`, checkoutData);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
      
    } catch (error) {
      console.error('Stripe checkout error:', error);
      throw error;
    }
  };

  const processChapaCheckout = async () => {
    try {
      const chapaData = {
        amount: convertedPrice,
        currency: 'ETB',
        email: customerInfo.email,
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone_number: customerInfo.phone,
        metadata: {
          type: 'subscription',
          tier_id: tier?.id || 'unknown',
          billing_cycle: billingCycle,
          original_amount_usd: basePrice.toString(),
          discounted_amount_usd: discountedPrice.toString(),
          discount_amount: discountCalculation?.discount_amount || 0,
          coupon_code: discountCalculation?.coupon?.code || null,
          affiliate_code: affiliateCode || null,
          click_id: localStorage.getItem('click_id') || null
        }
      };

      const response = await axios.post(`${API}/api/payments/chapa/initialize`, chapaData);
      
      // Chapa returns checkout_url nested in data.data.checkout_url
      const checkoutUrl = response.data.data?.checkout_url || response.data.checkout_url;
      
      if (response.data.status === 'success' && checkoutUrl) {
        // Redirect to Chapa Checkout
        window.location.href = checkoutUrl;
      } else {
        throw new Error(response.data.message || 'Failed to initialize Chapa payment');
      }
      
    } catch (error) {
      console.error('Chapa checkout error:', error);
      throw error;
    }
  };

  const handleTrialActivation = async () => {
    try {
      console.log('🚀 Starting trial activation...', {
        tier_id: tier.id,
        tier_name: tier.name,
        trial_days: tier.trial_days,
        billing_cycle: billingCycle,
        user: user?.email,
        has_token: !!token
      });

      // Check authentication
      if (!user || !token) {
        console.error('❌ User not authenticated');
        toast.error('Please login to start your free trial', {
          description: 'Redirecting to login...',
          duration: 3000
        });
        setTimeout(() => navigate('/login'), 1500);
        return;
      }

      // For trial subscriptions, use the secure start-trial endpoint.
      // The backend derives user_id from JWT and trial_days from the tier
      // (INSA hardening — clients cannot tamper with trial length).
      const subscriptionData = {
        tier_id: tier.id
      };

      console.log('📤 Sending trial request:', subscriptionData);

      const response = await axios.post(`${API}/api/subscriptions/start-trial`, subscriptionData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Trial subscription created:', response.data);

      toast.success(`${tier.trial_days}-day free trial activated! 🎉`, {
        description: 'Your trial subscription is now active. Redirecting...',
        duration: 3000
      });
      
      // Track affiliate conversion if applicable
      const affiliateCode = localStorage.getItem('affiliate_code') || localStorage.getItem('affiliate_ref');
      if (affiliateCode) {
        try {
          console.log('📊 Tracking affiliate conversion...');
          await axios.post(`${API}/api/affiliates/referrals/convert`, {
            affiliate_code: affiliateCode,
            subscription_tier: tier.name,
            amount: basePrice,
            click_id: localStorage.getItem('click_id') || null
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('✅ Affiliate conversion tracked');
        } catch (err) {
          console.error('⚠️ Failed to track affiliate conversion:', err);
        }
      }

      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Redirect to dashboard after short delay
      console.log('🔄 Redirecting to dashboard...');
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (error) {
      console.error('❌ Trial activation error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to activate trial';
      toast.error('Trial Activation Failed', {
        description: errorMessage,
        duration: 5000
      });
      
      throw error;
    }
  };

  const handleCheckout = async () => {
    try {
      // Validate required fields
      if (!customerInfo.email) {
        toast.error('Email is required');
        return;
      }

      if (paymentGateway === 'chapa' && (!customerInfo.firstName || !customerInfo.lastName)) {
        toast.error('First name and last name are required for Chapa payments');
        return;
      }

      setProcessing(true);

      // If tier has trial enabled, activate trial instead of processing payment
      if (tier.trial_enabled && tier.trial_days > 0) {
        await handleTrialActivation();
      } else {
        // Process regular payment
        if (paymentGateway === 'stripe') {
          await processStripeCheckout();
        } else {
          await processChapaCheckout();
        }
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(getErrorMessage(error, 'Failed to process checkout'));
      setProcessing(false);
    }
  };

  const isFormValid = () => {
    // For trials with authenticated users, form validation is not required
    if (user && tier?.trial_enabled && tier?.trial_days > 0) {
      return true;
    }
    
    // For regular payments, email is required
    if (!customerInfo.email) return false;
    if (paymentGateway === 'chapa') {
      return customerInfo.firstName && customerInfo.lastName;
    }
    return true;
  };

  // Don't render if no tier is selected
  if (!tier) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Complete Your Purchase
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{tier?.display_name || 'Subscription Plan'}</h3>
                    <p className="text-sm text-gray-600">{tier?.description || 'Plan description'}</p>
                    <Badge variant="outline" className="mt-2">
                      {billingCycle} billing
                    </Badge>
                  </div>
                  <div className="text-right">
                    {discountCalculation ? (
                      <div>
                        <div className="text-lg line-through text-gray-500">
                          {currency} {paymentGateway === 'chapa' && currencyRate 
                            ? (basePrice * currencyRate.rate).toFixed(2) 
                            : basePrice.toFixed(2)}
                        </div>
                        <div className="text-2xl font-bold text-green-600">
                          {currency} {convertedPrice.toFixed(2)}
                        </div>
                        <div className="text-sm text-green-600">
                          Save {currency} {paymentGateway === 'chapa' && currencyRate 
                            ? (discountCalculation.discount_amount * currencyRate.rate).toFixed(2)
                            : discountCalculation.discount_amount.toFixed(2)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-2xl font-bold">
                        {currency} {convertedPrice.toFixed(2)}
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      per {billingCycle === 'annual' ? 'year' : 'month'}
                    </div>
                  </div>
                </div>
                
                {/* Affiliate Tracking Display */}
                {affiliateCode && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800 font-medium">
                        Referred by affiliate: <code className="bg-green-100 px-2 py-1 rounded text-xs">{affiliateCode}</code>
                      </span>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      Your purchase will help support our affiliate partner
                    </p>
                  </div>
                )}

                {/* Coupon Input */}
                <div className="pt-4 border-t">
                  <CouponInput
                    amount={basePrice}
                    subscriptionTier={tier?.name || 'starter'}
                    currency="USD" // Always validate in USD, convert later
                    onDiscountApplied={handleDiscountApplied}
                    onDiscountRemoved={handleDiscountRemoved}
                    userId={customerInfo.email} // Use email as user identifier
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+251900000000"
                  />
                </div>
              </div>
              
              {paymentGateway === 'chapa' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      value={customerInfo.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      value={customerInfo.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment Method</CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={paymentGateway} 
                onValueChange={setPaymentGateway}
                className="space-y-3"
              >
                <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                  <RadioGroupItem value="stripe" id="stripe" />
                  <Label htmlFor="stripe" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Stripe</span>
                      <Badge variant="outline">International</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Credit/Debit cards, Apple Pay, Google Pay (USD)
                    </p>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2 p-3 border rounded hover:bg-gray-50">
                  <RadioGroupItem value="chapa" id="chapa" />
                  <Label htmlFor="chapa" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Chapa</span>
                      <Badge variant="outline">Ethiopia</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {rateLoading ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Loading live rates...
                        </span>
                      ) : currencyRate ? (
                        <span>
                          Ethiopian Birr (ETB) payments - {currencyRate.description}
                          <span className="text-xs text-gray-500 ml-1">
                            ({currencyRate.source})
                          </span>
                        </span>
                      ) : (
                        "Ethiopian Birr (ETB) payments - Rate: 1 USD ≈ 120 ETB (estimated)"
                      )}
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={processing}
              className="flex-1"
            >
              Cancel
            </Button>
            
            {/* Show login button if user not authenticated and trial enabled */}
            {!user && tier.trial_enabled && tier.trial_days > 0 ? (
              <Button 
                onClick={() => navigate('/login')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login to Start Free Trial
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={handleCheckout}
                disabled={processing || !isFormValid()}
                className="flex-1"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {tier.trial_enabled && tier.trial_days > 0 ? 'Activating Trial...' : 'Processing...'}
                  </>
                ) : tier.trial_enabled && tier.trial_days > 0 ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Start {tier.trial_days}-Day Free Trial
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay {currency} {convertedPrice.toFixed(2)}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </div>
          
          {/* Security Notice */}
          <div className="text-xs text-gray-500 text-center space-y-1">
            <p>🔒 Your payment information is secure and encrypted</p>
            <p>💳 You'll be redirected to complete payment securely</p>
            <p>📧 Confirmation email will be sent after successful payment</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DirectCheckout;